/**
 * The pieces one side has captured, grouped the way the old screen draws them
 * (`PlayChess/components/CapturedPieces/index.js` L28-143): one overlapping
 * stack per piece type, cheapest first, plus the material lead for whoever is
 * ahead.
 *
 * `selectCaptured` derives the list from the FEN and caches it ON the FEN, so a
 * clock tick cannot re-scan the board string (UX spec §6 rule 3) — and the
 * groups are `useMemo`'d on that cached object's identity, so the View keeps its
 * props (and its `React.memo`) for as long as the position stands.
 */
import {useMemo} from 'react';

import {useLiveGame} from '@/Store/liveGameStore';
import {PIECE_ORDER, materialLead} from '@/Store/liveGame/chess/captured';
import {
  selectBoardSource,
  selectCaptured,
  selectWatchCaptured,
} from '@/Store/liveGame/selectors';
import type {Side} from '@/Store/liveGame/state';

/** one stack: `code` is the FEN letter ('p' | 'P' …), `count` how many */
export type CapturedGroup = {code: string; count: number};

export type CapturedModel = {
  /** the pieces THIS side has taken, grouped by type in value order */
  groups: CapturedGroup[];
  /** the material this side is ahead by; 0 when level or behind */
  lead: number;
};

export function useCapturedModel(side: Side): CapturedModel {
  // M4: the watched tray is derived from the WATCHED fen, with its own
  // one-entry cache (`selectWatchCaptured`) — the wire always sends it empty.
  const source = useLiveGame(selectBoardSource);
  const captured = useLiveGame(
    source === 'watch' ? selectWatchCaptured : selectCaptured,
  );

  return useMemo(() => {
    // the pieces THIS side took are the pieces the OTHER colour is missing
    const mine = side === 'white' ? captured.b : captured.w;

    const counts = new Map<string, number>();
    mine.forEach(code => counts.set(code, (counts.get(code) || 0) + 1));

    const groups: CapturedGroup[] = [];
    PIECE_ORDER.forEach(type => {
      counts.forEach((count, code) => {
        if (code.toLowerCase() === type) {
          groups.push({code, count});
        }
      });
    });

    return {groups, lead: materialLead(captured)[side]};
  }, [captured, side]);
}
